/**
 * The backend's refusal of AI work the author has too few credits for. It answers such a request with
 * HTTP 402, and an envelope whose `reactName` is `notEnoughCredits` -- or `purchasedQuotaInDebt` when
 * the author's purchased credits are in debt beyond a limit the backend never shows. The agents tell
 * the refusal apart by the status and which one by the reactName, so a store can word it without
 * knowing about HTTP.
 *
 * Kept here, with no imports of its own, for the same reason `resolveErrorMessage` is: a store can
 * recognise the refusal without loading the request layer.
 */
export const PAYMENT_REQUIRED_STATUS = 402;

/** The reactName of a 402 refused because the author's purchased credits are in debt beyond the limit. */
export const PURCHASED_QUOTA_IN_DEBT_REACT_NAME = 'purchasedQuotaInDebt';

/**
 * Why AI work was refused: `noCredits`, too few credits in total; `inDebt`, purchased credits in debt
 * beyond the limit, whatever else the author holds -- buying is the way on.
 */
export type TCreditsRefusalReason = 'noCredits' | 'inDebt';

/** The reason a 402 carrying this reactName stands for. Anything but the debt's is a refusal for credits. */
export const creditsRefusalReasonOf = (reactName: string | undefined): TCreditsRefusalReason => (reactName === PURCHASED_QUOTA_IN_DEBT_REACT_NAME ? 'inDebt' : 'noCredits');

/**
 * Said when a course could not be started for want of credits and the backend sent no words of its
 * own; when it sent any, they are said instead.
 */
export const NOT_ENOUGH_CREDITS_MESSAGE = 'You do not have enough credits for this.';

/**
 * Said in the generation dialog when the backend refused an image for want of credits. The dialog's
 * own words rather than the backend's, because the status is the whole of what the media agent passes
 * on: the store words it, as it does the allowance beside it.
 */
export const NOT_ENOUGH_CREDITS_FOR_AN_IMAGE_MESSAGE = 'You do not have enough credits to generate this image.';

/** Said in the generation dialog when the backend refused an image for debt. Names no number: the limit is never shown. */
export const IN_DEBT_FOR_AN_IMAGE_MESSAGE = 'Buy credits to continue generating images.';

export class NotEnoughCreditsError extends Error {
  readonly reason: TCreditsRefusalReason;

  constructor(message: string = NOT_ENOUGH_CREDITS_MESSAGE, reason: TCreditsRefusalReason = 'noCredits') {
    super(message || NOT_ENOUGH_CREDITS_MESSAGE);
    this.name = 'NotEnoughCreditsError';
    this.reason = reason;
  }
}

export const isNotEnoughCredits = (error: unknown): error is NotEnoughCreditsError => error instanceof NotEnoughCreditsError;
